import { useQueryClient } from '@tanstack/react-query';
import { keys, useAlerts } from '../../api/queries';
import { useDismissAlert } from '../../api/mutations';
import { useSitemapAlerts } from '../../api/socket';
import { Ic } from '../../components/Ic';
import { relTime } from '../../lib/time';
import type { Alert, SitemapAlert } from '../../api/types';

const isSitemap = (a: Alert): a is SitemapAlert => a.type === 'new_url_discovered';

export function AlertFeedCard() {
  const queryClient = useQueryClient();
  const { data: alerts } = useAlerts();
  const dismiss = useDismissAlert();

  // Refetch rather than optimistically prepending the socket payload.
  //
  // The live event and the stored record are NOT the same object: the stored
  // one gains a `timestamp` field at push time that the socket payload lacks.
  // Dismissal deletes by exact JSON equality (Redis LREM), so a locally-held
  // socket payload could never be dismissed. Always show the server's copy.
  useSitemapAlerts(() => {
    void queryClient.invalidateQueries({ queryKey: keys.alerts });
  });

  const sitemapAlerts = (alerts ?? []).filter(isSitemap).slice(0, 6);

  return (
    <div className="pcard">
      <div className="head">
        <Ic.link className="ic" />
        <h3>New URLs discovered</h3>
        <span className="spacer" />
        <span className="pill">
          <span className="live-dot" style={{ marginRight: 4 }} />
          live
        </span>
      </div>
      <div className="body">
        {sitemapAlerts.length === 0 && (
          <div className="frow" style={{ borderBottom: 'none' }}>
            <div className="main">
              <div className="m">Nothing new from the sitemap scan.</div>
            </div>
          </div>
        )}
        {sitemapAlerts.map((alert) => (
          <div key={alert.url} className="frow">
            <div className="main">
              <div className="t">
                <a
                  href={alert.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{ color: 'inherit' }}
                >
                  {alert.url}
                </a>
              </div>
              <div className="m">
                <span>{alert.domain}</span>
                {alert.timestamp && (
                  <>
                    <span className="muted">·</span>
                    <span>{relTime(alert.timestamp)}</span>
                  </>
                )}
              </div>
            </div>
            <button
              className="btn ghost sm icon"
              title="Dismiss"
              aria-label={`Dismiss ${alert.url}`}
              disabled={dismiss.isPending}
              // `alert` is the exact object from the API response — passing a
              // reshaped copy here would silently fail to match on the server.
              onClick={() => dismiss.mutate({ source: 'sitemap', payload: alert })}
            >
              <Ic.x className="ico" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
